import s from '../styles/pages/main.module.scss'
import DefaultInput from "../elements/inputs/DefaultInput";
import BitrixUserSelector from "../elements/inputs/BitrixUserSelector";
import SubmitButton from "../elements/inputs/SubmitButton";
import InlineNotification from "../elements/InlineNotification";
import Header from "../elements/Header";
import {useCallback, useEffect, useMemo, useState} from "react";
import Loading from "../elements/Loading";
import OneSelectUI from "../elements/inputs/one-select";
import Popup from "../elements/popup";
import AllFieldsSelector from "../elements/fields-selector/AllFieldsSelector";
import moment from "moment";
import {ReactComponent as SettingsLogo} from '../assets/settings-logo.svg';
import {ReactComponent as JobLogo} from "../assets/job.svg";
import {ReactComponent as WindowLogo} from "../assets/window.svg";
import {ReactComponent as HeadhunterIcon} from "../assets/hh.svg";
import {ReactComponent as SubscriptionIcon} from '../assets/subscription.svg';
import {ReactComponent as GettingStartedIcon} from '../assets/rocket.svg';
import {useSearchParams} from "react-router-dom";
import getAPIClientMocked from "../hooks/apiClientMocked";
import InlineNotificationWithContent from "../elements/InlineNotificationWithContent";
import GettingStartedTab from "../elements/setupGuide";
import HhVacancyImport from "../elements/HhVacancyImport";
import getAPIClient from "../hooks/apiClient";
import React from 'react'

const downloadFileFromResponse = (response, defaultFilename = 'Счет.pdf') => {
    try {
        const contentDisposition = response.headers['content-disposition'];
        let filename = defaultFilename;

        // Парсим имя файла из заголовка
        if (contentDisposition) {
            // Пытаемся извлечь имя файла с utf-8 encoding
            const utf8FilenameMatch = contentDisposition.match(/filename\*=utf-8''([^;]+)/i);
            if (utf8FilenameMatch) {
                filename = decodeURIComponent(utf8FilenameMatch[1]);
            } else {
                // Пытаемся извлечь обычное имя файла
                const filenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
        }

        const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/octet-stream'
        });

        // Для браузеров
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
    } catch (error) {
        console.error('Ошибка при обработке файла:', error);
    }
};

// Компонент попапа оформления подписки
function SubscriptionPaymentPopup({BX24, api, isOpen, onClose, onInvoiceCreated, selectedPlanForPayment, extraUserPrice = 3500, extraUsers, setExtraUsers}) {
    const [companyQuery, setCompanyQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [isLoadingCompany, setIsLoadingCompany] = useState(false);

    const [contactName, setContactName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [subscriptionPeriod, setSubscriptionPeriod] = useState({value: 1, label: '1 месяц'});

    const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
    const [error, setError] = useState('');

    const plan = selectedPlanForPayment;
    const months = subscriptionPeriod.value;
    const extraUsersPrice = extraUsers * extraUserPrice;
    const netPrice = plan ? (plan.prices.ru.price + extraUsersPrice) * months : 0;
    const vatAmount = Math.round(netPrice * 0.05);
    const totalPrice = netPrice + vatAmount;

    // Поиск подсказок по компании
    useEffect(() => {
        if (!companyQuery || companyQuery.length < 2 || selectedCompany) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        const timer = setTimeout(() => {
            setIsLoadingCompany(true);
            api.get(`/bitrix_portals/subscriptions/company/search?q=${encodeURIComponent(companyQuery)}`)
                .then(r => {
                    setSuggestions(r.data);
                    setShowSuggestions(r.data.length > 0);
                })
                .catch(() => setSuggestions([]))
                .finally(() => setIsLoadingCompany(false));
        }, 300);
        return () => clearTimeout(timer);
    }, [companyQuery, selectedCompany, api]);

    const handleSelectCompany = (company) => {
        setSelectedCompany(company);
        setCompanyQuery(company.name);
        setShowSuggestions(false);
        setSuggestions([]);
        setError('');
    };

    const handleCompanyChange = (e) => {
        setCompanyQuery(e.target.value);
        setSelectedCompany(null);
        setError('');
    };

    const handleCreateInvoice = async () => {
        if (!companyQuery.trim() || !contactName.trim() || !email.trim() || !plan) {
            setError('Заполните все обязательные поля');
            return;
        }
        if (typeof window !== 'undefined' && window.ym) {
            window.ym(103768537, 'reachGoal', 'download_account_resume_scan');
        }
        try {
            setIsGeneratingInvoice(true);
            const bx24Params = BX24.getAllParams();
            const response = await api.post('/bitrix_portals/subscriptions/invoice', {
                company_name: companyQuery.trim(),
                inn: selectedCompany ? selectedCompany.inn : undefined,
                contact_name: contactName.trim(),
                email: email.trim(),
                phone: phone.trim() || undefined,
                domain: bx24Params.DOMAIN,
                plan_type: plan.id,
                number_of_months: months,
                extra_users: extraUsers,
            }, {responseType: 'blob'});
            await downloadFileFromResponse(response);
            if (onInvoiceCreated) onInvoiceCreated();
        } catch (err) {
            setError('Ошибка при формировании счёта. Попробуйте позже');
        } finally {
            setIsGeneratingInvoice(false);
        }
    };

    return (
        <Popup openedFlag={isOpen} setOpenedFlag={onClose}>
            <div className={s.payment_popup}>
                <h3>Счёт на подключение</h3>

                <div className={s.form_group}>
                    <div className={s.autocomplete_wrapper}>
                        <DefaultInput
                            title="Компания"
                            value={companyQuery}
                            onChangeFunction={handleCompanyChange}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                        />
                        {isLoadingCompany && <div className={s.loading_small}>Поиск...</div>}
                        {showSuggestions && (
                            <div className={s.suggestions_dropdown}>
                                {suggestions.map((c, i) => (
                                    <div key={i} className={s.suggestion_item}
                                         onMouseDown={() => handleSelectCompany(c)}>
                                        <div className={s.suggestion_name}>{c.name}</div>
                                        <div className={s.suggestion_meta}>
                                            ИНН {c.inn}{c.address ? ` · ${c.address.split(',')[0]}` : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className={s.form_row_2col}>
                    <DefaultInput
                        title="Имя"
                        value={contactName}
                        onChangeFunction={(e) => { setContactName(e.target.value); setError(''); }}
                    />
                    <DefaultInput
                        title="Почта"
                        value={email}
                        onChangeFunction={(e) => { setEmail(e.target.value); setError(''); }}
                    />
                </div>

                <div className={s.form_group}>
                    <DefaultInput
                        title="Телефон для связи — необязательно"
                        value={phone}
                        onChangeFunction={(e) => setPhone(e.target.value)}
                    />
                </div>

                <div className={s.form_group}>
                    <OneSelectUI
                        title="Период подписки"
                        selectionOptions={[
                            {value: 1, label: '1 месяц'},
                            {value: 3, label: '3 месяца'},
                            {value: 6, label: '6 месяцев'},
                            {value: 12, label: '1 год'},
                        ]}
                        selectedOption={subscriptionPeriod}
                        setSelectedOption={setSubscriptionPeriod}
                        placeholder="Выберите период"
                    />
                </div>

                {plan && (() => {
                    const baseUsers = plan.limits?.number_of_extension_users || 0;
                    const totalUsers = baseUsers + extraUsers;
                    return (
                        <div className={`${s.users_stepper} ${s.form_group}`}>
                            <div className={s.users_stepper_left}>
                                <div className={s.users_stepper_label}>
                                    Пользователей расширения: <span className={s.users_count}>{totalUsers}</span>
                                </div>
                                <div className={s.users_stepper_hint}>
                                    {baseUsers} включено, каждый следующий — {extraUserPrice.toLocaleString('ru-RU')} ₽/мес
                                </div>
                            </div>
                            <div className={s.stepper_controls}>
                                <button
                                    className={s.stepper_btn}
                                    onClick={() => setExtraUsers(v => Math.max(0, v - 1))}
                                    disabled={extraUsers === 0}
                                    type="button"
                                >−</button>
                                <button
                                    className={s.stepper_btn}
                                    onClick={() => setExtraUsers(v => v + 1)}
                                    type="button"
                                >+</button>
                            </div>
                        </div>
                    );
                })()}

                {plan && (
                    <div className={s.invoice_summary}>
                        <div className={s.summary_row}>
                            <span>Тариф «{plan.name}», {subscriptionPeriod.label}</span>
                            <span>{(plan.prices.ru.price * months).toLocaleString('ru-RU')} ₽</span>
                        </div>
                        {extraUsers > 0 && (
                            <div className={s.summary_row}>
                                <span>Доп. пользователи ({extraUsers} × {extraUserPrice.toLocaleString('ru-RU')} ₽{months > 1 ? ` × ${months} мес.` : ''})</span>
                                <span>{(extraUsersPrice * months).toLocaleString('ru-RU')} ₽</span>
                            </div>
                        )}
                        <div className={s.summary_row}>
                            <span>НДС 5%</span>
                            <span>{vatAmount.toLocaleString('ru-RU')} ₽</span>
                        </div>
                        <div className={`${s.summary_row} ${s.summary_total}`}>
                            <span>Итого к оплате</span>
                            <span>{totalPrice.toLocaleString('ru-RU')} ₽</span>
                        </div>
                    </div>
                )}

                <div className={s.agreement}>
                    <label>
                        <input type="checkbox" checked={agreed}
                               onChange={(e) => setAgreed(e.target.checked)}/>
                        <span>Отправляя заявку, я соглашаюсь с <a href="https://resumescan.ru/privacy/" target="_blank" rel="noopener noreferrer">политикой обработки ПДн</a> и даю <a href="https://resumescan.ru/consent/" target="_blank" rel="noopener noreferrer">согласие на обработку</a>.</span>
                    </label>
                </div>

                {error && <div className={s.error_message}>{error}</div>}

                <div className={s.actions}>
                    {isGeneratingInvoice ? (
                        <Loading small/>
                    ) : (
                        <SubmitButton
                            text="Скачать счёт"
                            onClick={handleCreateInvoice}
                            disabled={!agreed || !companyQuery.trim() || !contactName.trim() || !email.trim()}
                        />
                    )}
                    <button className={s.cancel_button} onClick={onClose}>Отмена</button>
                </div>
            </div>
        </Popup>
    );
}

function SubscriptionTab({api, BX24}) {
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [plans, setPlans] = useState([]);
    const [extraUserPrice, setExtraUserPrice] = useState(3500);
    const [extraUsers, setExtraUsers] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [showPaymentPopup, setShowPaymentPopup] = useState(false);
    const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);

    // Загрузка данных о подписке и тарифах
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [subscriptionResponse, plansResponse] = await Promise.all([
                    api.get('/bitrix_portals/subscriptions/status'),
                    api.get('/bitrix_portals/subscriptions')
                ]);

                setSubscriptionData(subscriptionResponse.data);
                setPlans(plansResponse.data.plans);
                setExtraUserPrice(plansResponse.data.extra_user_price);
            } catch (error) {
                console.error('Ошибка при получении данных:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    // Обработчик открытия попапа для конкретного плана
    const handleSubscribeClick = (plan) => {
        plan.label = plan.name
        plan.value = plan.id
        setSelectedPlanForPayment(plan);
        setShowPaymentPopup(true);
    };

    if (isLoading) {
        return <div className={s.subscription_loading}>Загрузка данных о подписке...</div>;
    }

    // Определяем текущий план.
    // Тариф может быть платным (есть в каталоге /subscriptions), пробным ('trial')
    // или отсутствовать. 'trial' и отсутствие подписки в каталог не входят, поэтому
    // для них строим синтетический план, а лимиты берём из /subscriptions/status.
    const currentPlanId = subscriptionData?.active ? subscriptionData.plan_type : 'none';
    const catalogPlan = plans.find(plan => plan.id === currentPlanId);
    const planLabels = {trial: 'Пробный период', none: 'Без активной подписки'};
    const currentPlan = catalogPlan
        ? {...catalogPlan, limits: subscriptionData?.limits ?? catalogPlan.limits}
        : {
            id: currentPlanId,
            name: planLabels[currentPlanId] || 'Без активной подписки',
            prices: {ru: {price: 0, price_without_discount: 0}},
            limits: subscriptionData?.limits ?? {
                number_of_analyses: 0,
                number_of_extension_users: 0,
                number_of_resume_updates: 0,
            },
        };

    // Функция для форматирования цены
    const formatPrice = (price, currency = 'руб./мес') => {
        return `${price.toLocaleString('ru-RU')} ${currency}`;
    };

    return (
        <div className={s.subscription_tab}>
            {/* Блок текущего тарифа */}
            <div className={s.current_plan_section}>
                <h2>Текущий тариф</h2>
                <div className={s.current_plan_card}>
                    <div className={s.plan_header}>
                        <h3>{currentPlan.name}</h3>
                        <div className={s.plan_price}>
                            {formatPrice(currentPlan.prices.ru.price)}
                            {currentPlan.prices.ru.price_without_discount > 0 && (
                                <div className={s.original_price}>
                                    {formatPrice(currentPlan.prices.ru.price_without_discount)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/*<div className={s.plan_features}>*/}
                    {/*    <ul>*/}
                    {/*        {currentPlan.features.map((feature, index) => (*/}
                    {/*            <li key={index}>*/}
                    {/*                <span className={s.feature_header}>{feature.name}</span>*/}
                    {/*                <div className={s.feature_text}>{feature.text}</div>*/}
                    {/*            </li>*/}
                    {/*        ))}*/}
                    {/*    </ul>*/}
                    {/*</div>*/}

                    {/* Информация об использовании */}
                    <div className={s.usage_info}>
                        <h4>Использование ресурсов</h4>
                        <div className={s.usage_grid}>
                            <div className={s.usage_item}>
                                <div className={s.usage_title}>
                                    <span className={s.usage_label}>AI-анализ резюме:</span>
                                    <span className={s.usage_value}>
                                      {subscriptionData.usage.analysis_calls} /
                                        {currentPlan.limits.number_of_analyses === null
                                            ? ' ∞'
                                            : ` ${currentPlan.limits.number_of_analyses}`}
                                    </span>
                                </div>
                                <div className={s.usage_bar}>
                                    <div
                                        className={s.usage_fill}
                                        style={{
                                            width: `${(subscriptionData.usage.analysis_calls /
                                                (currentPlan.limits.number_of_analyses || 1)) * 100}%`
                                        }}
                                    ></div>
                                </div>
                            </div>

                            <div className={s.usage_item}>
                                <div className={s.usage_title}>
                                    <span className={s.usage_label}>Пользователей расширения:</span>
                                    <span className={s.usage_value}>
                                  {subscriptionData.usage.extension_tokens} /
                                    {currentPlan.limits.number_of_extension_users === null
                                        ? ' ∞'
                                        : ` ${currentPlan.limits.number_of_extension_users}`}
                              </span>
                                </div>
                                <div className={s.usage_bar}>
                                    <div
                                        className={s.usage_fill}
                                        style={{
                                            width: `${(subscriptionData.usage.extension_tokens /
                                                (currentPlan.limits.number_of_extension_users || 1)) * 100}%`
                                        }}
                                    ></div>
                                </div>
                            </div>

                            <div className={s.usage_item}>
                                <div className={s.usage_title}>
                                    <span className={s.usage_label}>Обновлений резюме:</span>
                                    <span className={s.usage_value}>
                                      {subscriptionData.usage.update_resume_calls} /
                                        {currentPlan.limits.number_of_resume_updates === null
                                            ? ' ∞'
                                            : ` ${currentPlan.limits.number_of_resume_updates}`}
                                  </span>
                                </div>
                                <div className={s.usage_bar}>
                                    <div
                                        className={s.usage_fill}
                                        style={{
                                            width: `${(subscriptionData.usage.update_resume_calls /
                                                (currentPlan.limits.number_of_resume_updates || 1)) * 100}%`
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {subscriptionData?.active && (
                        <div className={s.subscription_info}>
                            <div className={s.info_item}>
                                <span>Статус:</span>
                                <span className={s.info_value}>Активен</span>
                            </div>
                            <div className={s.info_item}>
                                <span>Действует до:</span>
                                <span className={s.info_value}>
                                {moment(subscriptionData.end_date).format('DD.MM.YYYY')}
                            </span>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            <div className={s.pricing_section}>
                <h2>Тарифные планы</h2>
                <div className={s.pricing_grid}>
                    {plans.map(plan => (
                        <PlanCard
                            key={plan.id}
                            plan={plan}
                            isCurrent={plan.id === currentPlanId}
                            subscriptionData={subscriptionData}
                            onSubscribeClick={() => handleSubscribeClick(plan)}
                            extraUsers={extraUsers}
                            setExtraUsers={setExtraUsers}
                            extraUserPrice={extraUserPrice}
                        />
                    ))}
                    <div className={s.pricing_right_col}>
                        <TrialCard subscriptionData={subscriptionData} />
                        <IndividualPlanCard />
                    </div>
                </div>
            </div>

            <SubscriptionPaymentPopup
                BX24={BX24}
                api={api}
                isOpen={showPaymentPopup}
                onClose={() => setShowPaymentPopup(false)}
                selectedPlanForPayment={selectedPlanForPayment}
                extraUserPrice={extraUserPrice}
                extraUsers={extraUsers}
                setExtraUsers={setExtraUsers}
            />
        </div>
    );
}

SubscriptionTab = React.memo(SubscriptionTab)

function PlanCard({plan, isCurrent, subscriptionData, onSubscribeClick, extraUsers = 0, setExtraUsers, extraUserPrice = 3500}) {
    const price = plan.prices.ru.price;
    const originalPrice = plan.prices.ru.price_without_discount;
    const discountPct = originalPrice && originalPrice > price
        ? Math.round((1 - price / originalPrice) * 100)
        : 0;
    const baseUsers = plan.limits.number_of_extension_users ?? 0;
    const totalUsers = baseUsers + extraUsers;
    const dynamicPrice = price + extraUsers * extraUserPrice;
    const dynamicOriginalPrice = originalPrice ? originalPrice + extraUsers * extraUserPrice : 0;

    return (
        <div className={[
            s.pricing_card,
            isCurrent ? s.pricing_card_current : '',
            plan.extra.highlight ? s.pricing_card_highlighted : '',
        ].join(' ')}>
            {discountPct > 0 && <div className={s.discount_badge}>−{discountPct}%</div>}
            {isCurrent && <div className={s.current_badge}>Текущий тариф</div>}
            <div className={s.pricing_card_name}>{plan.name}</div>
            {discountPct > 0 && (
                <div className={s.promo_badge}>Акция — при оплате до 30 июня</div>
            )}
            <div className={s.pricing_card_price}>
                <div className={s.price_main_row}>
                    <div className={s.price_main}>
                        {dynamicPrice.toLocaleString('ru-RU')}&nbsp;<span className={s.price_currency}>₽/мес</span>
                    </div>
                    {discountPct > 0 && <span className={s.price_period_note}>за первый месяц</span>}
                </div>
                {dynamicOriginalPrice > 0 && dynamicOriginalPrice !== dynamicPrice && (
                    <div className={s.price_then}>далее — {dynamicOriginalPrice.toLocaleString('ru-RU')} ₽/мес · НДС 5% сверху</div>
                )}
            </div>
            <div className={s.plan_limits}>
                <div className={s.users_stepper}>
                    <div className={s.users_stepper_left}>
                        <div className={s.users_stepper_label}>
                            Пользователей расширения: <span className={s.users_count}>{totalUsers}</span>
                        </div>
                        <div className={s.users_stepper_hint}>
                            {baseUsers} включено, каждый следующий — {extraUserPrice.toLocaleString('ru-RU')} ₽/мес
                        </div>
                    </div>
                    <div className={s.stepper_controls}>
                        <button className={s.stepper_btn} type="button"
                            onClick={() => setExtraUsers(v => Math.max(0, v - 1))}
                            disabled={extraUsers === 0}>−</button>
                        <button className={s.stepper_btn} type="button"
                            onClick={() => setExtraUsers(v => v + 1)}>+</button>
                    </div>
                </div>
                <div className={s.plan_limit_item}>
                    <span>✦ AI-анализ — до <strong>{plan.limits.number_of_analyses ?? '∞'}</strong> проверок в месяц</span>
                </div>
            </div>
            <div className={s.pricing_feature_grid}>
                {plan.features.map((f, i) => (
                    <div key={i} className={s.pricing_feature_item}>
                        <span className={s.pricing_check}>✓</span>
                        <span>{f.name}{f.text ? ` — ${f.text}` : ''}</span>
                    </div>
                ))}
            </div>
            <div className={s.pricing_card_footer}>
                {isCurrent ? (
                    <button className={`${s.pricing_cta} ${s.cta_renew}`} onClick={onSubscribeClick}>
                        Продлить
                    </button>
                ) : (
                    <button className={`${s.pricing_cta} ${s.cta_subscribe}`} onClick={onSubscribeClick}>
                        {subscriptionData?.active ? 'Перейти' : 'Оформить'}
                    </button>
                )}
            </div>
        </div>
    );
}

function TrialCard({subscriptionData}) {
    const isActive = subscriptionData?.active && subscriptionData?.plan_type === 'trial';
    const endDate = isActive && subscriptionData?.end_date
        ? moment(subscriptionData.end_date).format('DD.MM.YYYY')
        : null;

    return (
        <div className={`${s.pricing_card} ${s.trial_card} ${!isActive ? s.trial_card_expired : ''}`}>
            <div className={s.trial_card_header}>
                <span className={s.pricing_card_name}>Пробный период</span>
                <span className={`${s.trial_badge} ${isActive ? s.trial_badge_active : s.trial_badge_expired}`}>
                    {isActive ? 'Активен' : 'Завершён'}
                </span>
            </div>
            <div className={s.trial_limits}>
                <div>100 AI-анализов</div>
                <div>1 пользователь расширения</div>
                <div>3 дня</div>
            </div>
            {endDate && <div className={s.trial_end}>Действует до {endDate}</div>}
        </div>
    );
}

function IndividualPlanCard() {
    return (
        <div className={`${s.pricing_card} ${s.pricing_card_individual}`}>
            <div className={s.pricing_card_name}>Индивидуальный</div>
            <div className={`${s.pricing_feature_grid} ${s.pricing_feature_grid_1col}`}>
                {[
                    'Тариф, настроенный под ваши задачи',
                    'Доработки под ваши процессы',
                    'Расширенные лимиты',
                    'Приоритетная техподдержка',
                ].map((f, i) => (
                    <div key={i} className={s.pricing_feature_item}>
                        <span className={s.pricing_check}>✓</span><span>{f}</span>
                    </div>
                ))}
            </div>
            <div className={s.pricing_card_footer}>
                <a href="https://helprobot.ru/resumescan" target="_blank" rel="noopener noreferrer"
                   className={`${s.pricing_cta} ${s.cta_contact}`}>
                    Связаться
                </a>
            </div>
        </div>
    );
}

export default function MainPage({BX24}) {
    const [isLoading, setIsLoading] = useState(true)

    // Флаги, которые говорят что юзер что-то поменял после последнего получения данных
    const [spUpdatedFlag, setSpUpdatedFlag] = useState(false)
    const [spVacanciesUpdatedFlag, setSpVacanciesUpdatedFlag] = useState(false)
    const [mappingUpdatedFlag, setMappingUpdatedFlag] = useState(false)

    const [fieldsPopupSelectorOpenedFlag, setFieldsPopupSelectorOpenedFlag] = useState(false)

    const [notificationMessage, setNotificationMessage] = useState('')
    const [notificationType, setNotificationType] = useState('error')

    const api = useMemo(() => getAPIClient(BX24), [])
    // const api = useMemo(() => getAPIClientMocked(), [])

    // Смарт процессы
    const [smartProcessesList, setSmartProcessesList] = useState([])
    useEffect(() => {
        api.get('/setup/smart_processes_from_portal').then(response => {
            let sp_list = []
            for (let i in response.data) {
                let sp = response.data[i]
                sp.label = sp.title
                sp.value = `${sp.local_smart_process_id}_${sp.smart_process_type}`
                sp_list.push(sp)
            }
            setSmartProcessesList(sp_list)
        }).finally(setIsLoading(false))
    }, [api])

    const [selectedSmartProcess, setSelectedSmartProcess] = useState(undefined)
    const [selectedVacanciesSmartProcess, setSelectedVacanciesSmartProcess] = useState(undefined)

    // Список отношений (данные -> поле)
    const [dataToFieldMapping, setDataToFieldMapping] = useState([])
    useEffect(() => {
        if (selectedSmartProcess === undefined || selectedSmartProcess === null) return
        setIsLoading(true)
        api.get(
            `/portal_fields_mappings?local_b24_smart_process_id=${selectedSmartProcess.local_smart_process_id}&smart_process_type=${selectedSmartProcess.smart_process_type}`
        ).then(response => {
            let mapping_list = []
            for (let i in response.data) {
                let mapping_item = response.data[i]
                mapping_item.label = mapping_item.ui_name
                mapping_item.value = mapping_item.system_name
                mapping_list.push(mapping_item)
            }
            setDataToFieldMapping(mapping_list)
        }).finally(() => setIsLoading(false))
    }, [selectedSmartProcess])

    // Список доступных полей на портале по смарт процессу резюме
    const [availableFieldsInSmartProcess, setAvailableFieldsInSmartProcess] = useState([])
    useEffect(() => {
        if (selectedSmartProcess === undefined || selectedSmartProcess === null) return
        api.get(
            `/setup/fields_smart_process?local_b24_smart_process_id=${selectedSmartProcess.local_smart_process_id}&sp_type=${selectedSmartProcess.smart_process_type}&smart_process_id=${selectedSmartProcess.smart_process_id}`
        ).then(response => {
            let fields_list = []
            for (let i in response.data) {
                let field = response.data[i]
                field.label = field.field_name
                field.value = field.b24_system_name
                fields_list.push(field)
            }
            setAvailableFieldsInSmartProcess(fields_list)
        })
    }, [selectedSmartProcess])

    // Список доступных полей на портале по смарт процессу вакансий
    const [availableFieldsInVacanciesSmartProcess, setAvailableFieldsInVacanciesSmartProcess] = useState([])
    useEffect(() => {
        if (selectedVacanciesSmartProcess === undefined || selectedVacanciesSmartProcess === null) {
            setAvailableFieldsInVacanciesSmartProcess([])
            setSelectedFieldNameVacanciesHHID(null)
            return
        }
        api.get(
            `/setup/fields_smart_process?local_b24_smart_process_id=${selectedVacanciesSmartProcess.local_smart_process_id}&sp_type=${selectedVacanciesSmartProcess.smart_process_type}&smart_process_id=${selectedVacanciesSmartProcess.smart_process_id}`
        ).then(response => {
            let fields_list = []
            for (let i in response.data) {
                let field = response.data[i]
                field.label = field.field_name
                field.value = field.b24_system_name
                if (field.field_type === 'string') {
                    fields_list.push(field)
                }
            }
            setAvailableFieldsInVacanciesSmartProcess(fields_list)
        })
    }, [selectedVacanciesSmartProcess])

    const [selectedFieldNameVacanciesHHID, setSelectedFieldNameVacanciesHHID] = useState(undefined)
    const [selectedFieldVacanciesHHID, setSelectedFieldVacanciesHHID] = useState(undefined)


    useEffect(() => {
        if (!!selectedFieldNameVacanciesHHID && !!availableFieldsInVacanciesSmartProcess) {
            let selectedField = availableFieldsInVacanciesSmartProcess.filter(
                (field) => selectedFieldNameVacanciesHHID === field.b24_system_name
            )[0]
            setSelectedFieldVacanciesHHID(selectedField)
        } else {
            setSelectedFieldVacanciesHHID(null)
        }
    }, [selectedFieldNameVacanciesHHID, availableFieldsInVacanciesSmartProcess])


    // Если где-то в маппинге указано б24 поле, которого не существует в списке полей с бэка,
    // то удаляем это поле в маппинге
    const [wrongMappingClearedFlag, setWrongMappingClearedFlag] = useState(false)

    useEffect(() => {
        console.log('called', wrongMappingClearedFlag, dataToFieldMapping, availableFieldsInSmartProcess)

        if (dataToFieldMapping.length === 0 || availableFieldsInSmartProcess.length === 0) {
            return
        }
        if (wrongMappingClearedFlag) {
            return
        }
        setWrongMappingClearedFlag(true)

        let copiedDataToFieldMapping = JSON.parse(JSON.stringify(dataToFieldMapping))
        for (let i in copiedDataToFieldMapping) {
            let selectedField = availableFieldsInSmartProcess.filter(
                (field) => copiedDataToFieldMapping[i].b24_system_name === field.b24_system_name
            )[0]

            if (selectedField === undefined) {
                copiedDataToFieldMapping[i]['b24_system_name'] = null
            }
        }
        setDataToFieldMapping(copiedDataToFieldMapping)
    }, [dataToFieldMapping, availableFieldsInSmartProcess])


    // Счетчик для отображения на кнопке
    const currentConfiguredFieldsCounter = useMemo(() => {
        let counter = 0;
        for (let i in dataToFieldMapping) {
            if (dataToFieldMapping[i].b24_system_name !== null) {
                counter += 1
            }
        }
        return counter
    }, [dataToFieldMapping])


    const [globalNotificationMessage, setGlobalNotificationMessage] = useState('')
    const [globalNotificationType, setGlobalNotificationType] = useState('error')

    useEffect(() => {
        let paidUpdateReadFlag = !!localStorage.getItem('paidUpdateReadFlag')
        if (!paidUpdateReadFlag) {
            setGlobalNotificationType('success')
            setGlobalNotificationMessage(<>
                <b>Уважаемые клиенты!</b><br/><br/>
                С 15 июня 2026 года ResumeScan переходит на подписочную модель оплаты для всех клиентов. Это поможет нам развивать сервис и делать его еще лучше. <br/>
                Ознакомиться с новыми тарифами и возможностями можно по <a href={'https://helprobot.ru/resumescan?utm_source=news_push&utm_medium=tarif&utm_campaign=resume_scan#rec790198865'} target='_blank'>ссылке</a><br/>
                <br/>
                Спасибо, что вы с нами! 💙
                <button className={s.close_button} onClick={() => {
                    setGlobalNotificationMessage('')
                    localStorage.setItem('paidUpdateReadFlag', 'true')
                }}>✖</button>
            </>)
        }
    }, [])

    // Получение конфига, установка текущего СП
    useEffect(() => {
        if (smartProcessesList === undefined || smartProcessesList.length === 0)
            return

        api.get(`/bitrix_portals/settings`).then(response => {
            if (response?.data?.local_smart_process_id) {
                let currentSP = JSON.parse(JSON.stringify(smartProcessesList.filter(
                    (el) => response.data.local_smart_process_id === el.local_smart_process_id && (response.data.smart_process_type === el.smart_process_type)
                )[0]))
                setSelectedSmartProcess(currentSP)
            }
            if (response?.data?.vacancies_local_smart_process_id) {
                let currentVacanciesSP = JSON.parse(JSON.stringify(smartProcessesList.filter(
                    (el) => response.data.vacancies_local_smart_process_id === el.local_smart_process_id && (response.data.smart_process_type === el.smart_process_type)
                )[0]))
                setSelectedVacanciesSmartProcess(currentVacanciesSP)
            }
            if (response?.data?.vacancies_hh_id_system_name) {
                setSelectedFieldNameVacanciesHHID(response.data.vacancies_hh_id_system_name)
            }
        })
    }, [smartProcessesList])

    // Обновление конфигурации приложения (смарт-процесс, маппинги)
    const saveChanges = useCallback(async () => {
        let errorText = ''
        setNotificationMessage('')

        // отправка запрос на обновление settings
        if (spUpdatedFlag || spVacanciesUpdatedFlag) {
            try {
                let data = JSON.parse(JSON.stringify(selectedSmartProcess))
                data['vacancies_local_smart_process_id'] = selectedVacanciesSmartProcess?.local_smart_process_id ? selectedVacanciesSmartProcess.local_smart_process_id : null
                data['vacancies_smart_process_id'] = selectedVacanciesSmartProcess?.smart_process_id ? selectedVacanciesSmartProcess.smart_process_id : null
                data['vacancies_smart_process_type'] = selectedVacanciesSmartProcess?.smart_process_type ? selectedVacanciesSmartProcess.smart_process_type : null
                data['vacancies_hh_id_system_name'] = selectedFieldVacanciesHHID?.b24_system_name ? selectedFieldVacanciesHHID.b24_system_name : null

                console.log('data', data)

                await api.post(`/bitrix_portals/settings`, data)
                setSpUpdatedFlag(false)
                setSpVacanciesUpdatedFlag(false)
            } catch (e) {
                errorText = 'Ошибка при обновлении выбранного СП; '
            }
        }

        // Отправляем запрос на обновление mapping
        if (mappingUpdatedFlag) {
            try {
                await api.put(`/portal_fields_mappings`, dataToFieldMapping)
                setMappingUpdatedFlag(false)
            } catch (e) {
                errorText = errorText + 'Ошибка при обновлении маппинга данных к полям'
            }
        }

        // Дорабатываем сообщение с ошибкой, если таковые имеются
        if (errorText.length !== 0) {
            errorText += 'Вы можете написать в тех. поддержку, нажав на кнопку "Чат поддержки"'
            setNotificationType('error')
            setNotificationMessage(errorText)
        } else {
            setNotificationType('success')
            setNotificationMessage('Данные успешно обновлены')
        }
    }, [selectedSmartProcess, selectedVacanciesSmartProcess, selectedFieldVacanciesHHID, dataToFieldMapping, spUpdatedFlag, spVacanciesUpdatedFlag, mappingUpdatedFlag])

    const setupAddons = useCallback(async () => {
        try {
            await api.post(`/bitrix_portals/settings/setup-addons`)
            setNotificationType('success')
            setNotificationMessage('Встроенные приложения ResumeScan успешно сконфигурированы!')
        } catch (e) {
            setNotificationType('error')
            setNotificationMessage('Произошла ошибка при попытке конфигурации встроенных приложений')
        }
    }, [])

    // ------ Работа с токенами ------

    const [tokens, setTokens] = useState([])

    // --- Взаимодействие с апи

    // Функция для получения списка токенов
    const apiFetchTokens = useCallback(async () => {
        let response = await api.get(`/extension_tokens`)
        return response.data
    }, [api])

    // Функция для удаления токена
    const apiDeleteToken = useCallback(async (token_id) => {
        await api.delete(`/extension_tokens/${token_id}`)
    }, [api])

    // Функция для создания токена
    const apiCreateToken = useCallback(async (token_name, responsible) => {
        let data = {
            token_name: token_name
        }
        if (responsible) {
            data['responsible_id'] = responsible.id
            data['responsible_name'] = responsible.name
        }
        let response = await api.post(`/extension_tokens`, data)
        return response.data
    }, [api])

    // --- Функции для всего процесса получения, изменения, удаления токенов

    // Первичное получение всех токенов
    useEffect(() => {
        apiFetchTokens().then(fetchedTokens => setTokens(fetchedTokens.items))
    }, [apiFetchTokens])

    // Удаление токена
    const deleteToken = useCallback(async (token_id) => {
        setTokenError('')
        await apiDeleteToken(token_id)
        let fetchedTokens = await apiFetchTokens()
        setTokens(fetchedTokens.items)
    }, [apiDeleteToken, apiFetchTokens, setTokens])

    // Создание токена
    const [newTokenName, setNewTokenName] = useState('')
    const [newTokenResponsible, setNewTokenResponsible] = useState()
    const [tokenError, setTokenError] = useState()
    const createToken = useCallback(async () => {
        setNewTokenName('')
        setNewTokenResponsible(undefined)
        try {
            await apiCreateToken(newTokenName, newTokenResponsible)
            let fetchedTokens = await apiFetchTokens()
            setTokens(fetchedTokens.items)
            setTokenError('')
        } catch (e) {
            setTokenError(e.response.data.detail)
        }
    }, [apiCreateToken, newTokenName, newTokenResponsible])

    // Автоматизация hh
    const [searchParams, setSearchParams] = useSearchParams()

    const [localEmployerName, setLocalEmployerName] = useState('')
    const [HHClientID, setHHClientID] = useState(undefined)
    const HHRedirectURL = useMemo(() => `${window.location.origin}/hh_authorize`, [])
    const [isHHAuthorized, setIsHHAuthorized] = useState(false)
    const [HHAuthorizationInfo, setHHAuthorizationInfo] = useState()
    const [hhNotificationType, setHhNotificationType] = useState('error')
    const [hhNotificationMessage, setHhNotificationMessage] = useState('')
    const [allDirections, setAllDirections] = useState(undefined)
    const [HHsettingsUpdatedFlag, setHHsettingsUpdatedFlag] = useState(false)
    const [responsesSelectedDirection, setResponsesSelectedDirection] = useState(undefined)
    const [hhResponsesCategoryId, setHhResponsesCategoryId] = useState(null)
    const [responsesSelectedAssignedById, setResponsesSelectedAssignedById] = useState(undefined)
    const [responsesSelectedAssignedByName, setResponsesSelectedAssignedByName] = useState(undefined)

    useEffect(() => {
        api.get('/services_interactions/hh/app_authorization_data').then((r) => {
            setHHClientID(r.data.hh_client_id)
        }).catch((e) => {
            setHhNotificationMessage('Произошла ошибка при попытке получить данные для авторизации. Пожалуйста, напишите в ТП')
            setNotificationType('error')
        })
    }, [])

    // Получение направлений (категорий) смарт процесса
    useEffect(() => {
        if (!selectedSmartProcess) {
            return
        }
        api.get(
            `/setup/categories_smart_process?local_b24_smart_process_id=${selectedSmartProcess.local_smart_process_id}&b24_smart_process_id=${selectedSmartProcess.smart_process_id}&smart_process_type=${selectedSmartProcess.smart_process_type}`
        ).then((r) => {
            let category_list = []
            for (let i in r.data) {
                let category = r.data[i]
                category['value'] = category['direction_id']
                category['label'] = category['direction_name']
                category_list.push(category)
            }
            setAllDirections(category_list)
        }).catch((e) => {
            let errorMessage = e?.response?.data?.detail
            if (!errorMessage)
                errorMessage = 'Пожалуйста, обратитесь в ТП.'
            setHhNotificationMessage(`Произошла ошибка при получении списка направлений смарт процесса. ${errorMessage}`)
            setHhNotificationType('error')
        })
    }, [selectedSmartProcess])

    // Получение текущих данных о настройках автоматизации hh.ru
    const getHhInteractionData = useCallback(() => {
        api.get('/services_interactions/hh').then((r) => {
            const data = r.data
            if (localEmployerName && data.employer_name && data.is_authorized && localEmployerName !== data.employer_name) {
                setHhNotificationMessage(<p>Указанное вами название компании <b>«{localEmployerName}»</b> не
                    соответствует названию компании из авторизованного аккаунта «<b>{data.employer_name}</b>». Обратите
                    внимание, возможно, вы авторизовались не с того аккаунта</p>)
                setHhNotificationType('error')
            }
            setIsHHAuthorized(data.is_authorized)
            setHHAuthorizationInfo(r.data)
            if (!data.is_authorized && data.access_token !== null) {
                setHhNotificationType('error')
                setHhNotificationMessage('Произошла ошибка - слетела авторизация hh.ru. Авторизуйтесь пожалуйста снова')
            }
            setResponsesSelectedAssignedById(data.responses_b24_assigned_by_id)
            setResponsesSelectedAssignedByName(data.responses_b24_assigned_by_name)
            setHhResponsesCategoryId(data.responses_b24_category_id)
        }).catch((error) => {
            let errorMessage = error?.response?.data?.detail
            if (!errorMessage)
                errorMessage = 'Пожалуйста, обратитесь в ТП.'
            setHhNotificationMessage(`Произошла ошибка при получении данных интеграции с hh.ru. ${errorMessage}`)
            setHhNotificationType('error')
        })
    }, [localEmployerName])


    useEffect(() => {
        if (!hhResponsesCategoryId || !allDirections?.length) return
        const found = allDirections.find(d => `${d.direction_id}` === `${hhResponsesCategoryId}`)
        if (found) setResponsesSelectedDirection(found)
    }, [hhResponsesCategoryId, allDirections])

    useEffect(() => {
        if (isHHAuthorized)
            return

        getHhInteractionData()
        const id = setInterval(() => {
            getHhInteractionData()
        }, 3000);
        return () => clearInterval(id);
    }, [getHhInteractionData, isHHAuthorized])


    // Деавторизация на hh.ru
    const deauthorizeHH = useCallback(() => {
        api.post('/services_interactions/hh/unauthorize').then((r) => {
            setHhNotificationMessage('Вы деавторизовались на hh.ru')
            setHhNotificationType('success')
            setIsHHAuthorized(false)
        }).catch((error) => {
            let errorMessage = error?.response?.data?.detail
            if (!errorMessage)
                errorMessage = 'Пожалуйста, обратитесь в ТП.'
            setHhNotificationMessage(`Ошибка при деавторизации на hh.ru. ${errorMessage}`)
            setHhNotificationType('error')
        })
    }, [])


    const postNewHHSettings = useCallback(() => {
        const data = {
            hh_auto_add_responses_flag: !!responsesSelectedDirection,
            responses_b24_assigned_by_id: !!responsesSelectedAssignedById ? `${responsesSelectedAssignedById}` : null,
            responses_b24_assigned_by_name: !!responsesSelectedAssignedByName ? `${responsesSelectedAssignedByName}` : null,
            responses_b24_category_id: !!responsesSelectedDirection ? `${responsesSelectedDirection.direction_id}` : null
        }
        console.log('data', data)
        api.put('/services_interactions/hh', data).then(() => {
            setHHsettingsUpdatedFlag(false)
            setHhNotificationType('success')
            setHhNotificationMessage('Данные интеграции hh.ru успешно обновлены')
        }).catch((e) => {
            let errorMessage = e?.response?.data?.detail
            if (!errorMessage)
                errorMessage = 'Пожалуйста, обратитесь в ТП.'
            setHhNotificationType('error')
            setHhNotificationMessage(`Ошибка при обновлении данных интеграции hh.ru. ${errorMessage}`)
        })
    }, [responsesSelectedDirection, responsesSelectedAssignedById, responsesSelectedAssignedById])

    const [selectedMenuItem, setSelectedMenuItem] = useState('getting-started') // settings, vacancies, extension, hh

    return (
        <>
            <Loading isLoading={isLoading}/>
            <Header/>
            <div className={s.main}>
                <InlineNotificationWithContent
                    type={globalNotificationType}
                    hideFlag={!globalNotificationMessage}
                    className={s.global_notification}
                >{globalNotificationMessage}</InlineNotificationWithContent>
                <div className={s.main_container}>
                    <div className={s.left_side_menu}>
                        <div className={s.menu_list}>
                            <div
                                className={s.menu_item + ' ' + (selectedMenuItem === 'getting-started' ? s.active_menu_item : '')}
                                onClick={() => setSelectedMenuItem('getting-started')}>
                                <div className={s.menu_item_icon}><GettingStartedIcon/></div>
                                <div className={s.menu_item_text}>С чего начать</div>
                            </div>
                            <div
                                className={s.menu_item + ' ' + (selectedMenuItem === 'settings' ? s.active_menu_item : '')}
                                onClick={() => setSelectedMenuItem('settings')}
                            >
                                <div className={s.menu_item_icon}><SettingsLogo/></div>
                                <div className={s.menu_item_text}>Настройка соискателей</div>
                            </div>
                            <div
                                className={s.menu_item + ' ' + (selectedMenuItem === 'vacancies' ? s.active_menu_item : '')}
                                onClick={() => setSelectedMenuItem('vacancies')}
                            >
                                <div className={s.menu_item_icon}><JobLogo/></div>
                                <div className={s.menu_item_text}>Настройка вакансий</div>
                            </div>
                            <div
                                className={s.menu_item + ' ' + (selectedMenuItem === 'hh_import' ? s.active_menu_item : '')}
                                onClick={() => setSelectedMenuItem('hh_import')}
                            >
                                <div className={s.menu_item_icon}><HeadhunterIcon/></div>
                                <div className={s.menu_item_text}>Импорт вакансии</div>
                            </div>
                            <div
                                className={s.menu_item + ' ' + (selectedMenuItem === 'extension' ? s.active_menu_item : '')}
                                onClick={() => setSelectedMenuItem('extension')}
                            >
                                <div className={s.menu_item_icon}><WindowLogo/></div>
                                <div className={s.menu_item_text}>Работа с расширением</div>
                            </div>
                            <div className={s.menu_item + ' ' + (selectedMenuItem === 'hh' ? s.active_menu_item : '')}
                                 onClick={() => setSelectedMenuItem('hh')}
                            >
                                <div className={s.menu_item_icon}><HeadhunterIcon/></div>
                                <div className={s.menu_item_text}>Автоматизация hh.ru</div>
                            </div>
                            {/* Пункт меню для подписки */}
                            <div
                                className={s.menu_item + ' ' + (selectedMenuItem === 'subscription' ? s.active_menu_item : '')}
                                onClick={() => setSelectedMenuItem('subscription')}
                            >
                                <div className={s.menu_item_icon}><SubscriptionIcon/></div>
                                <div className={s.menu_item_text}>Подписка</div>
                            </div>
                        </div>
                    </div>
                    <div className={s.active_block}>
                        {(selectedMenuItem === 'getting-started') && <GettingStartedTab BX24={BX24}/>}
                        {(selectedMenuItem === 'settings') && <div className={s.settings}>
                            <div className={s.settings_item}>
                                <OneSelectUI
                                    title={'Выберите, куда сохранять резюме'}
                                    selectionOptions={smartProcessesList}
                                    selectedOption={selectedSmartProcess}
                                    setSelectedOption={(selected) => {
                                        setSelectedSmartProcess(selected)
                                        setSpUpdatedFlag(true)
                                    }}
                                    placeholder={'Выберите, куда будут попадать резюме'}
                                />
                            </div>
                            <div className={s.settings_item}>
                                <div className={s.title}>
                                    Выберите поля в этой сущности, в которые будут заноситься данные
                                </div>
                                <button className={s.fields_selector_popup_button} onClick={
                                    () => {
                                        setFieldsPopupSelectorOpenedFlag(true)
                                        setMappingUpdatedFlag(true)
                                    }
                                }>
                                    {currentConfiguredFieldsCounter === 0
                                        ? 'Настроить сохраняемые данные'
                                        : `${currentConfiguredFieldsCounter}/${dataToFieldMapping.length} Соотнесено данных`
                                    }
                                </button>
                                <Popup openedFlag={fieldsPopupSelectorOpenedFlag}
                                       setOpenedFlag={setFieldsPopupSelectorOpenedFlag}>
                                    {fieldsPopupSelectorOpenedFlag && <AllFieldsSelector
                                        dataToFieldMapping={dataToFieldMapping}
                                        setDataToFieldMapping={setDataToFieldMapping}
                                        availableFieldsInSmartProcess={availableFieldsInSmartProcess}
                                        comment={'Вы сможете настроить/изменить эти данные позже'}
                                    />}
                                </Popup>
                            </div>
                            <div className={s.right_button}>
                                <SubmitButton
                                    onClick={saveChanges}
                                    disabled={!mappingUpdatedFlag && !spUpdatedFlag}
                                    className={s.submit}
                                    text={'Сохранить'}
                                />
                            </div>
                            <div className={s.settings_item}>
                                <div className={s.title}>Если у вас не появились встроенные приложения в карточках,
                                    нажмите сюда
                                </div>
                                <SubmitButton
                                    onClick={setupAddons}
                                    text={'Сконфигурировать встроенные приложения'}
                                />
                            </div>
                            <InlineNotification
                                type={notificationType}
                                text={notificationMessage}
                                hideFlag={notificationMessage.length === 0}
                            />
                        </div>}
                        {(selectedMenuItem === 'vacancies') && <div className={s.settings}>
                            <div className={s.settings_item}>
                                <OneSelectUI
                                    isClearable={true}
                                    title={'Выберите, откуда брать вакансии для связки в резюме'}
                                    selectionOptions={smartProcessesList}
                                    selectedOption={selectedVacanciesSmartProcess}
                                    setSelectedOption={(selected) => {
                                        if (selected === null || selected === undefined || selected.local_smart_process_id !== selectedVacanciesSmartProcess?.local_smart_process_id) {
                                            setSelectedFieldNameVacanciesHHID(null)
                                        }
                                        setSelectedVacanciesSmartProcess(selected)
                                        setSpVacanciesUpdatedFlag(true)
                                    }}
                                    placeholder={'Выберите откуда брать вакансии'}
                                />
                            </div>
                            <div className={s.settings_item}>
                                <OneSelectUI
                                    isClearable={true}
                                    title={'Укажите поле, в котором хранится ID вакансии с сервиса HeadHunter (нужен для автоматического закрепления откликов за вакансиями)'}
                                    selectionOptions={availableFieldsInVacanciesSmartProcess}
                                    selectedOption={selectedFieldVacanciesHHID}
                                    setSelectedOption={(selected) => {
                                        console.log('selected', selected)
                                        setSelectedFieldNameVacanciesHHID(selected?.b24_system_name)
                                        setSpVacanciesUpdatedFlag(true)
                                    }}
                                    placeholder={'Введите для поиска...'}
                                />
                            </div>
                            <div className={s.settings_item}>
                                <SubmitButton
                                    onClick={saveChanges}
                                    disabled={!mappingUpdatedFlag && !spUpdatedFlag && !spVacanciesUpdatedFlag}
                                    className={s.submit}
                                    text={'Сохранить'}
                                />
                            </div>
                            <InlineNotification
                                type={notificationType}
                                text={notificationMessage}
                                hideFlag={notificationMessage.length === 0}
                            />
                        </div>}
                        {(selectedMenuItem === 'extension') && <div className={s.extension_settings}>
                            <div className={s.download_extension_row}>
                                <div className={s.extension_description}>
                                    Расширение даёт возможность автоматически заносить резюме в CRM, когда вы находитесь
                                    на странице соискателя hh.ru
                                </div>
                                <button
                                    className={s.download_extension_button}
                                    onClick={() => window.open(
                                        'https://chromewebstore.google.com/detail/resumescan/cadadmdnamampppdldkkodghcjdfgihf',
                                        '_blank'
                                    )}
                                >
                                    Скачать расширение
                                </button>
                            </div>
                            <div className={s.tokens_editor}>
                                <div className={s.extension_header}>
                                    Список лицензионных ключей расширения
                                </div>
                                <div className={s.tokens_popup_add_row}>
                                    <DefaultInput
                                        className={s.tokens_popup_add_row_input}
                                        title={'Введите название для нового токена'}
                                        value={newTokenName}
                                        onChangeFunction={(e) => setNewTokenName(e.target.value)}
                                    />
                                    <BitrixUserSelector
                                        className={s.tokens_add_responsible_selector}
                                        selectedUser={newTokenResponsible}
                                        setSelectedUser={setNewTokenResponsible}
                                        BX24={BX24}
                                        notSelectedText={'Ответственный не выбран'}
                                    />
                                    <SubmitButton
                                        onClick={createToken}
                                        text={'+'}
                                    />
                                </div>
                                {tokenError && <InlineNotification type={'error'} text={tokenError} hideFlag={false}
                                                                   className={s.tokens_error}/>}
                                <table className={s.tokens_list}>
                                    <thead className={'tokens-list-header'}>
                                    <tr>
                                        <th>Название</th>
                                        <th>Лицензионный ключ</th>
                                        <th>Ответственный</th>
                                        <th>Дата создания</th>
                                        <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {tokens.map(token => <tr className={'token'}>
                                        <td>{token.token_name}</td>
                                        <td>{token.access_token}</td>
                                        <td>{token.responsible_id ? token.responsible_name : '[Нет]'}</td>
                                        <td>{moment.utc(token.created_at).local().format('DD.MM.YYYY HH:mm')}</td>
                                        <td className={s.token_delete} onClick={() => deleteToken(token.id)}>✖</td>
                                    </tr>)}

                                    </tbody>
                                </table>
                            </div>
                        </div>}
                        {(selectedMenuItem === 'hh') && <div className={s.hh_automatization}>
                            {!isHHAuthorized && <>
                                <div className={s.title}>
                                    Авторизуйтесь на hh.ru с аккаунта главного менеджера для настройки автоматического
                                    занесения откликов и других
                                    автоматизаций
                                </div>
                                <DefaultInput
                                    title={'Введите название компании на hh.ru'}
                                    value={localEmployerName}
                                    onChangeFunction={(e) => {
                                        setLocalEmployerName(e.target.value)
                                    }}
                                />
                                <p className={s.right_hint}>Узнать название компании можно <a
                                    href={'https://nn.hh.ru/employer/edit/simple?hhtmFrom=main&hhtmFromLabel=header'}
                                    target="_blank">здесь</a></p>
                                <button
                                    className={s.hh_authorize_button}
                                    onClick={() => {
                                        const bx24Params = BX24.getAllParams()
                                        let data = {
                                            "X-ACCESS-TOKEN": bx24Params.AUTH_ID,
                                            "X-ACCESS-TOKEN-EXPIRES": bx24Params.AUTH_EXPIRES,
                                            "X-REFRESH-TOKEN": bx24Params.REFRESH_ID,
                                            "X-PORTAL-MEMBER-ID": bx24Params.MEMBER_ID,
                                            "X-PORTAL-DOMAIN": bx24Params.DOMAIN,
                                        }
                                        window.open(
                                            'https://hh.ru/oauth/authorize?' +
                                            `response_type=code&` +
                                            `client_id=${HHClientID}&` +
                                            `redirect_uri=${HHRedirectURL}&` +
                                            `state=${JSON.stringify(data)}`,
                                            '_blank',
                                            'popup'
                                        )
                                    }}
                                    disabled={!localEmployerName}
                                >
                                    АВТОРИЗОВАТЬСЯ НА HH.RU
                                </button>
                                <InlineNotification
                                    type={hhNotificationType}
                                    text={hhNotificationMessage}
                                    hideFlag={hhNotificationMessage.length === 0}
                                />
                            </>}
                            {isHHAuthorized && <>
                                <div className={s.title}>
                                    Настройте автоматический сбор откликов
                                </div>
                                <div className={s.company_card}>
                                    <div className={s.company_header}>
                                        <HeadhunterIcon className={s.company_icon}/>
                                        <h3>Подключенный аккаунт hh.ru</h3>
                                        <span className={s.status_badge}>Авторизован</span>
                                    </div>

                                    <div className={s.company_details}>
                                        <div className={s.detail_item}>
                                            <span className={s.detail_label}>Компания:</span>
                                            <span className={s.detail_value}>
                        <a
                            href={`https://nn.hh.ru/employer/${HHAuthorizationInfo?.employer_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={s.company_link}
                        >
                            {HHAuthorizationInfo?.employer_name}
                        </a>
                    </span>
                                        </div>

                                        <div className={s.detail_item}>
                                            <span className={s.detail_label}>ID работодателя:</span>
                                            <span className={s.detail_value}>{HHAuthorizationInfo?.employer_id}</span>
                                        </div>
                                    </div>
                                </div>

                                <OneSelectUI
                                    title={'Выберите направление для сохранения откликов. Если не выбрано - отклики не собираются'}
                                    isClearable={true}
                                    placeholder={'Направление смарт-процесса'}
                                    selectionOptions={allDirections}
                                    selectedOption={responsesSelectedDirection}
                                    setSelectedOption={(selectedOption) => {
                                        setHHsettingsUpdatedFlag(true)
                                        setResponsesSelectedDirection(selectedOption)
                                    }}
                                />
                                <BitrixUserSelector
                                    title={'Выберите отвественного за новые отклики'}
                                    selectedUser={{
                                        name: responsesSelectedAssignedByName,
                                        id: responsesSelectedAssignedById
                                    }}
                                    setSelectedUser={(data) => {
                                        setResponsesSelectedAssignedByName(data.name)
                                        setResponsesSelectedAssignedById(data.id)
                                        setHHsettingsUpdatedFlag(true)
                                    }}
                                    BX24={BX24}
                                />
                                <SubmitButton
                                    className={s.save_hh_settings}
                                    text={'Сохранить'}
                                    disabled={!HHsettingsUpdatedFlag}
                                    onClick={postNewHHSettings}
                                />
                                <a className={s.deauth_link} onClick={deauthorizeHH} href={'#'}>Деавторизоваться</a>
                                <InlineNotification
                                    type={hhNotificationType}
                                    text={hhNotificationMessage}
                                    hideFlag={hhNotificationMessage.length === 0}
                                />
                            </>}
                        </div>}
                        {(selectedMenuItem === 'hh_import') && <HhVacancyImport BX24={BX24}/>}
                        {(selectedMenuItem === 'subscription') && <SubscriptionTab api={api} BX24={BX24}/>}
                    </div>
                </div>
            </div>
        </>
    )
}